import React, { useState, useEffect } from "react";
import TPC_URL from "../../../utils/TPC_URL";
import axios from "axios";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
  Typography,
  Box,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const InnerContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 20px;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const ChartContainer = styled.div`
  width: 80%;
  height: 400px;
  margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

function KpiPage() {
  const { group_code, project_code } = useParams();
  const [kpiData, setKpiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const currentYear = new Date().getFullYear().toString();
  useEffect(() => {
    const fetchKpiData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          TPC_URL +
            `/api/kpi/getOrderKpi?group_code=${group_code}&project_code=${project_code}&dateString=${currentYear}`
        );
        if (response.data) {
          const formattedData = response.data.map((item) => ({
            ...item,
            monthName: `${parseInt(item.month_year.split("-")[1])}월`,
          }));
          setKpiData(formattedData);
        }
        setLoading(false);
      } catch (err) {
        console.error("KPI 데이터 로딩 중 오류 발생:", err);
        setError("데이터를 불러오는 중 오류가 발생했습니다.");
        setLoading(false);
      }
    };
    fetchKpiData();
  }, [group_code, project_code, currentYear]);
  const calculateChangeRate = (changeCount, totalCount) => {
    if (!totalCount) return "0.0%";
    return ((changeCount / totalCount) * 100).toFixed(1) + "%";
  };
  const handleOpenModal = () => {
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
  };
  if (loading) {
    return (
      <Wrapper>
        <FrameContainer>
          <LoadingContainer>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              KPI 데이터를 불러오는 중...
            </Typography>
          </LoadingContainer>
        </FrameContainer>
      </Wrapper>
    );
  }
  if (error) {
    return (
      <Wrapper>
        <FrameContainer>
          <LoadingContainer>
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          </LoadingContainer>
        </FrameContainer>
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              {currentYear}년 주문 정보 지표 요약
            </Typography>
          </ToolbarLeft>
          <ToolbarRight>{/* 오른쪽 부분*/}</ToolbarRight>
        </ToolbarContainer>
        <InnerContainer>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={kpiData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="monthName" />
                <YAxis />
                <Tooltip
                  formatter={(value, name) => {
                    const nameMap = {
                      total_count: "전체 주문 수",
                      status_1_count: "상태 변경",
                      date_change_count: "생산일 변경",
                      quantity_change_count: "수량 변경",
                      both_change_count: "생산일+수량 변경",
                    };
                    return [`${value} 건`, nameMap[name] || name];
                  }}
                  labelFormatter={(label) => `${label}`}
                />
                <Legend />
                <Bar dataKey="total_count" name="전체 주문 수" fill="#8884d8" />
                <Bar dataKey="status_1_count" name="상태 변경" fill="#82ca9d" />
                <Bar
                  dataKey="date_change_count"
                  name="생산일 변경"
                  fill="#ffc658"
                />
                <Bar
                  dataKey="quantity_change_count"
                  name="수량 변경"
                  fill="#ff8042"
                />
                <Bar
                  dataKey="both_change_count"
                  name="생산일+수량 변경"
                  fill="#0088fe"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
          <ButtonContainer>
            <Button
              variant="contained"
              onClick={handleOpenModal}
              disabled={kpiData.length === 0}
              sx={{
                height: 40,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                backgroundColor: "var(--mui-palette-primary-main)",
                "&:hover": {
                  backgroundColor: "var(--mui-palette-primary-dark)",
                },
              }}
            >
              자세히 보기
            </Button>
          </ButtonContainer>
        </InnerContainer>
      </FrameContainer>
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="detail-modal-title"
        aria-describedby="detail-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "40%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxHeight: "80vh",
            overflow: "auto",
            borderRadius: 2,
          }}
        >
          <Typography
            id="detail-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
            align="left"
          >
            {currentYear}년 월별 주문 변경 상세 데이터
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="simple table">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    구분
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    전체 주문 수
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    전체 상태 변경률
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    생산일 변경률
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    수량 변경률
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    생산일+수량 변경률
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {kpiData
                  .filter((row) => row.total_count > 0)
                  .map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        backgroundColor:
                          index % 2 === 0 ? "inherit" : "#f9f9f9",
                      }}
                    >
                      <TableCell align="center" component="th" scope="row">
                        {row.monthName}
                      </TableCell>
                      <TableCell align="center">{row.total_count}</TableCell>
                      <TableCell align="center">
                        {calculateChangeRate(
                          row.status_1_count,
                          row.total_count
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {calculateChangeRate(
                          row.date_change_count,
                          row.total_count
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {calculateChangeRate(
                          row.quantity_change_count,
                          row.total_count
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {calculateChangeRate(
                          row.both_change_count,
                          row.total_count
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleCloseModal}
              size="small"
              sx={{
                height: 35,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              닫기
            </Button>
          </Box>
        </Box>
      </Modal>
    </Wrapper>
  );
}

export default KpiPage;
